import {
	type Camera,
	Controls,
	MathUtils,
	MOUSE,
	type OrthographicCamera,
	type PerspectiveCamera,
	Quaternion,
	Vector2,
	Vector3
} from 'three';

/**
 * CameraControls 事件载荷。
 *
 * 当前事件只携带事件名称本身，使用 `object` 可以避免空对象类型在 `dispatchEvent()` 中被过度收窄。
 */
type CameraControlsControlsEventPayload = object;

/**
 * CameraControls 会派发的事件映射。
 */
interface CameraControlsControlsEventMap {
	/** 相机已经被控制器更新。 */
	change: CameraControlsControlsEventPayload;
	/** 用户交互已经开始。 */
	start: CameraControlsControlsEventPayload;
	/** 用户交互已经结束。 */
	end: CameraControlsControlsEventPayload;
}

/**
 * CameraControls 支持的相机类型。
 */
type CameraControlsCamera = PerspectiveCamera | OrthographicCamera;


/**
 * CameraControls 旋转中心解析器。
 *
 * 设计说明：
 * 1. 控制器只负责相机数学，不直接读取 Viewer 或场景对象；
 * 2. 上层模块通过解析器注入当前旋转中心，避免 CameraControls 反向依赖场景实现；
 * 3. 入参 `target` 是可复用写入缓存，解析器应优先写入并返回该向量，减少旋转热路径对象创建。
 */
export type CameraControlsRotationCenterResolver = ( target: Vector3 ) => Vector3 | null;


/**
 * 控制器屏幕区域信息。
 */
interface CameraControlsScreen {
	/** DOM 元素左侧相对页面的偏移。 */
	left: number;
	/** DOM 元素顶部相对页面的偏移。 */
	top: number;
	/** DOM 元素宽度。 */
	width: number;
	/** DOM 元素高度。 */
	height: number;
}

/**
 * 鼠标按钮配置。
 */
interface CameraControlsMouseButtons {
	/** 左键对应的 three.js 鼠标动作。 */
	LEFT?: MOUSE | null;
	/** 中键对应的 three.js 鼠标动作。 */
	MIDDLE?: MOUSE | null;
	/** 右键对应的 three.js 鼠标动作。 */
	RIGHT?: MOUSE | null;
}

/**
 * CameraControls 内部有限状态机状态。
 */
enum CameraControlsControlState {
	/** 当前没有正在执行的交互。 */
	NONE = -1,
	/** 鼠标旋转相机状态。 */
	ROTATE = 0,
	/** 鼠标缩放相机状态。 */
	ZOOM = 1,
	/** 鼠标平移相机状态。 */
	PAN = 2,
	/** 触控旋转相机状态。 */
	TOUCH_ROTATE = 3,
	/** 触控缩放和平移相机状态。 */
	TOUCH_ZOOM_PAN = 4
}


/**
 * CameraControls 支持的鼠标操作枚举。
 */
enum CameraControlsMouseOperation {
	/** 旋转相机。 */
	ROTATE = 'ROTATE',
	/** 缩放相机。 */
	ZOOM = 'ZOOM',
	/** 平移相机。 */
	PAN = 'PAN'
}

/**
 * CameraControls 支持的鼠标输入枚举。
 */
enum CameraControlsMouseInput {
	/** 鼠标左键。 */
	LEFT = 0,
	/** 鼠标中键。 */
	MIDDLE = 1,
	/** 鼠标右键。 */
	RIGHT = 2,
	/** 鼠标滚轮。 */
	WHEEL = 'WHEEL'
}

/**
 * CameraControls 支持的鼠标修饰键枚举。
 */
enum CameraControlsMouseModifier {
	/** 按下 Ctrl 键或 Command 键。 */
	CTRL = 'CTRL',
	/** 按下 Shift 键。 */
	SHIFT = 'SHIFT'
}

/**
 * 鼠标操作公开入参类型。
 */
type CameraControlsMouseActionOperation = 'ROTATE' | 'ZOOM' | 'PAN';

/**
 * 鼠标输入公开入参类型。
 */
type CameraControlsMouseActionMouse = 0 | 1 | 2 | 'WHEEL';

/**
 * 鼠标修饰键公开入参类型。
 */
type CameraControlsMouseActionKey = 'CTRL' | 'SHIFT';

/**
 * 鼠标操作配置项。
 */
interface CameraControlsMouseAction {
	/** 需要执行的相机操作。 */
	operation: CameraControlsMouseActionOperation;
	/** 触发操作的鼠标输入。 */
	mouse: CameraControlsMouseActionMouse;
	/** 触发操作所需的键盘修饰键。 */
	key: CameraControlsMouseActionKey | null;
	/** 操作对应的内部状态。 */
	state: CameraControlsControlState;
}


/**
 * 相机已经被控制器更新时派发。
 */
const _changeEvent = { type: 'change' } as const;

/**
 * 用户交互开始时派发。
 */
const _startEvent = { type: 'start' } as const;

/**
 * 用户交互结束时派发。
 */
const _endEvent = { type: 'end' } as const;

/**
 * 浮点比较容差。
 */
const _EPS = 0.000001;

/** 惯性单步角度低于此弧度后一次结算剩余几何级数，停止无效尾帧。 */
const ROTATION_INERTIA_ANGLE_EPSILON = 1e-8;

/**
 * 当前运行平台是否为 macOS。
 */
const _isMac = /Mac/.test( globalThis?.navigator?.platform );


type CameraControlsOperationStateMap = Record<CameraControlsMouseActionOperation, CameraControlsControlState>;

/**
 * 鼠标操作到 CameraControls 内部状态的映射。
 */
const _OPERATION_STATE: CameraControlsOperationStateMap = Object.freeze( {
	[ CameraControlsMouseOperation.ROTATE ]: CameraControlsControlState.ROTATE,
	[ CameraControlsMouseOperation.ZOOM ]: CameraControlsControlState.ZOOM,
	[ CameraControlsMouseOperation.PAN ]: CameraControlsControlState.PAN
} );


/**
 * 判断相机是否为透视相机。
 *
 * @param camera - 需要判断的 Three.js 相机。
 * @returns 如果相机为透视相机则返回 `true`。
 */
function isPerspectiveCamera( camera: Camera ): camera is PerspectiveCamera {
	return ( camera as PerspectiveCamera ).isPerspectiveCamera === true;
}

/**
 * 判断相机是否为正交相机。
 *
 * @param camera - 需要判断的 Three.js 相机。
 * @returns 如果相机为正交相机则返回 `true`。
 */
function isOrthographicCamera( camera: Camera ): camera is OrthographicCamera {

	return ( camera as OrthographicCamera ).isOrthographicCamera === true;

}

const _v2 = new Vector2();
const _mouseChange = new Vector2();
const _objectUp = new Vector3();
const _pan = new Vector3();
const _axis = new Vector3();
const _quaternion = new Quaternion();
const _eyeDirection = new Vector3();
const _objectUpDirection = new Vector3();
const _objectSidewaysDirection = new Vector3();
const _moveDirection = new Vector3();
const _cursorZoomNDC = new Vector3();
const _cursorZoomRayOrigin = new Vector3();
const _cursorZoomRayDirection = new Vector3();
const _cursorZoomPlaneNormal = new Vector3();
const _cursorZoomDelta = new Vector3();

/**
 * CameraControls 与 {@link OrbitControls} 类似，但不会强制保持相机 `up` 向量恒定。
 * 因此相机绕过视图极点时不会自动翻转回“正立”方向。
 *
 * @augments Controls
 * @three_import import { CameraControls } from 'three/addons/controls/CameraControls.js';
 */
export  class CameraControls extends Controls<CameraControlsControlsEventMap> {

	/** 当前运动已捕获的世界中心只存数值，不保留场景或拓扑对象。 */
	private rotationCenterCaptured = false;

	/** 由控制器管理的相机对象。 */
	declare object: CameraControlsCamera;

	/** 当前 DOM 元素屏幕区域缓存。 */
	screen: CameraControlsScreen;

	/** 旋转速度。 */
	rotateSpeed: number;

	/** 拖拽缩放速度。 */
	zoomSpeed: number;

	/** 滚轮缩放速度。 */
	dollySpeed: number;

	/** 平移速度。 */
	panSpeed: number;

	/** 是否禁用旋转。 */
	noRotate: boolean;

	/** 是否禁用缩放。 */
	noZoom: boolean;

	/** 是否禁用平移。 */
	noPan: boolean;

	/** 是否禁用动态阻尼。 */
	staticMoving: boolean;

	/** 动态阻尼系数。 */
	dynamicDampingFactor: number;

	/** 透视相机最小距离。 */
	minDistance: number;

	/** 透视相机最大距离。 */
	maxDistance: number;

	/** 正交相机最小缩放值。 */
	minZoom: number;

	/** 正交相机最大缩放值。 */
	maxZoom: number;

	/** 是否启用光标中心缩放。 */
	cursorZoom: boolean;

	/** 键盘控制状态映射。 */
	keys: [ string, string, string ];

	/** three.js 兼容鼠标按钮配置。 */
	mouseButtons: CameraControlsMouseButtons;

	/** 鼠标输入、修饰键与控制状态映射。 */
	mouseActions: CameraControlsMouseAction[];

	/** 控制器目标点。 */
	target: Vector3;


	/** 当前旋转中心解析器；为空时沿用原有 target 作为旋转中心。 */
	rotationCenterResolver: CameraControlsRotationCenterResolver | null;

	/** 当前旋转中心缓存，避免旋转热路径重复创建 Vector3。 */
	readonly _rotationCenter: Vector3;

	/** target 相对旋转中心的偏移缓存。 */
	readonly _rotationCenterTargetOffset: Vector3;


	/** 当前交互状态。 */
	state: CameraControlsControlState;

	/** 当前键盘覆盖状态。 */
	keyState: CameraControlsControlState;

	/** 上一次相机位置，用于判断是否派发 change。 */
	readonly _lastPosition: Vector3;

	/** 上一次相机缩放值，用于判断正交相机是否变化。 */
	_lastZoom: number;

	/** 触控缩放起始距离。 */
	_touchZoomDistanceStart: number;

	/** 触控缩放结束距离。 */
	_touchZoomDistanceEnd: number;

	/** 上一次旋转角度，用于阻尼惯性。 */
	_lastAngle: number;

	/** 相机到目标点的方向向量。 */
	readonly _eye: Vector3;

	/** 上一次旋转输入点。 */
	readonly _movePrev: Vector2;

	/** 当前旋转输入点。 */
	readonly _moveCurr: Vector2;

	/** 上一次旋转轴，用于阻尼惯性。 */
	readonly _lastAxis: Vector3;

	/** 缩放起始输入点。 */
	readonly _zoomStart: Vector2;

	/** 缩放结束输入点。 */
	readonly _zoomEnd: Vector2;

	/** 滚轮缩放倍率缓存。 */
	_wheelZoomFactor: number;

	/** 当前是否正在执行光标中心缩放。 */
	_cursorZoomActive: boolean;

	/** 光标中心缩放的世界坐标锚点。 */
	readonly _cursorZoomPoint: Vector3;

	/** 平移起始输入点。 */
	readonly _panStart: Vector2;

	/** 平移结束输入点。 */
	readonly _panEnd: Vector2;

	/** 当前是否启用鼠标世界平移。 */
	_mousePanActive: boolean;

	/** 鼠标世界平移的页面坐标缓存。 */
	readonly _mousePanCursor: Vector2;

	/** 鼠标世界平移起点。 */
	readonly _mousePanStart: Vector3;

	/** 鼠标世界平移终点。 */
	readonly _mousePanEnd: Vector3;

	/** 当前参与交互的指针列表。 */
	readonly _pointers: PointerEvent[];

	/** 指针 ID 到页面坐标的映射。 */
	readonly _pointerPositions: Record<number, Vector2>;

	/** 绑定后的指针移动监听。 */
	readonly _onPointerMove: ( event: PointerEvent ) => void;

	/** 绑定后的指针按下监听。 */
	readonly _onPointerDown: ( event: PointerEvent ) => void;

	/** 绑定后的指针抬起监听。 */
	readonly _onPointerUp: ( event: PointerEvent ) => void;

	/** 绑定后的指针取消监听。 */
	readonly _onPointerCancel: ( event: PointerEvent ) => void;

	/** 绑定后的右键菜单监听。 */
	readonly _onContextMenu: ( event: Event ) => void;

	/** 绑定后的滚轮监听。 */
	readonly _onMouseWheel: ( event: WheelEvent ) => void;

	/** 绑定后的键盘按下监听。 */
	readonly _onKeyDown: ( event: KeyboardEvent ) => void;

	/** 绑定后的键盘抬起监听。 */
	readonly _onKeyUp: () => void;

	/** 绑定后的触控开始监听。 */
	readonly _onTouchStart: ( event: PointerEvent ) => void;

	/** 绑定后的触控移动监听。 */
	readonly _onTouchMove: ( event: PointerEvent ) => void;

	/** 绑定后的触控结束监听。 */
	readonly _onTouchEnd: ( event: PointerEvent ) => void;

	/** 绑定后的鼠标按下监听。 */
	readonly _onMouseDown: ( event: PointerEvent ) => boolean;

	/** 绑定后的鼠标移动监听。 */
	readonly _onMouseMove: ( event: PointerEvent ) => void;

	/** 绑定后的鼠标抬起监听。 */
	readonly _onMouseUp: () => void;

	/** 初始目标点，用于 reset。 */
	readonly _target0: Vector3;

	/** 初始相机位置，用于 reset。 */
	readonly _position0: Vector3;

	/** 初始相机上方向，用于 reset。 */
	readonly _up0: Vector3;

	/** 初始相机缩放值，用于 reset。 */
	readonly _zoom0: number;

	/**
	 * 创建新的 CameraControls 控制器实例。
	 *
	 * @param object - 控制器管理的相机。
	 * @param domElement - 用于注册事件监听的 DOM 元素。
	 */
	constructor( object: CameraControlsCamera, domElement: HTMLElement | SVGElement | null = null ) {

		super( object, domElement );

		/**
		 * 表示当前控制器 DOM 元素的屏幕区域，会在调用 `handleResize()` 时自动刷新。
		 *
		 * @type {Object}
		 * @readonly
		 */
		this.screen = { left: 0, top: 0, width: 0, height: 0 };

		/**
		 * 旋转速度。
		 *
		 * @type {number}
		 * @default 1
		 */
		this.rotateSpeed = 1.0;

		/**
		 * 拖拽缩放速度。
		 *
		 * @type {number}
		 * @default 1.2
		 */
		this.zoomSpeed = 1.2;


		/**
		 * 鼠标滚轮缩放速度。该值会参与指数缩放倍率计算，和 CameraControls 的 `dollySpeed` 语义保持一致。
		 *
		 * @type {number}
		 * @default 1
		 */
		this.dollySpeed = 1.0;


		/**
		 * 平移速度。
		 *
		 * @type {number}
		 * @default 0.3
		 */
		this.panSpeed = 0.3;

		/**
		 * 是否禁用旋转。
		 *
		 * @type {boolean}
		 * @default false
		 */
		this.noRotate = false;

		/**
		 * 是否禁用缩放。
		 *
		 * @type {boolean}
		 * @default false
		 */
		this.noZoom = false;

		/**
		 * 是否禁用平移。
		 *
		 * @type {boolean}
		 * @default false
		 */
		this.noPan = false;

		/**
		 * 是否禁用动态阻尼。
		 *
		 * @type {boolean}
		 * @default false
		 */
		this.staticMoving = false;

		/**
		 * 动态阻尼强度，仅在 `staticMoving` 为 `false` 时生效。
		 *
		 * @type {number}
		 * @default 0.2
		 */
		this.dynamicDampingFactor = 0.2;

		/**
		 * 透视相机允许拉近的最小距离。
		 *
		 * @type {number}
		 * @default 0
		 */
		this.minDistance = 0;

		/**
		 * 透视相机允许拉远的最大距离。
		 *
		 * @type {number}
		 * @default Infinity
		 */
		this.maxDistance = Infinity;

		/**
		 * 正交相机允许放大的最小缩放值。
		 *
		 * @type {number}
		 * @default 0
		 */
		this.minZoom = 0;

		/**
		 * 正交相机允许缩小的最大缩放值。
		 *
		 * @type {number}
		 * @default Infinity
		 */
		this.maxZoom = Infinity;


		/**
		 * 是否以光标所在位置为中心执行缩放。
		 *
		 * @type {boolean}
		 * @default true
		 */
		this.cursorZoom = true;


		/**
		 * 保存用于切换交互模式的键盘编码。
		 *
		 * - 按下第一个按键时，所有鼠标交互执行旋转。
		 * - 按下第二个按键时，所有鼠标交互执行缩放。
		 * - 按下第三个按键时，所有鼠标交互执行平移。
		 *
		 * 默认值为 *KeyA, KeyS, KeyD*，分别表示 A、S、D。
		 *
		 * @type {Array<string>}
		 */
		this.keys = [ 'KeyA' /*A*/, 'KeyS' /*S*/, 'KeyD' /*D*/ ];

		/**
		 * 保存 three.js 兼容的鼠标按钮动作引用。
		 *
		 * ```js
		 * controls.mouseButtons = {
		 * 	LEFT: THREE.MOUSE.ROTATE,
		 * 	MIDDLE: THREE.MOUSE.DOLLY,
		 * 	RIGHT: THREE.MOUSE.PAN
		 * }
		 * ```
		 * @type {Object}
		 */
		this.mouseButtons = { LEFT: MOUSE.ROTATE, MIDDLE: MOUSE.DOLLY, RIGHT: MOUSE.PAN };


		/**
		 * 保存鼠标输入、修饰键和相机操作之间的映射关系。
		 *
		 * @type {Array<Object>}
		 */
		this.mouseActions = [];

		this.setMouseAction( 'ROTATE', 0 );
		this.setMouseAction( 'ZOOM', 1 );
		this.setMouseAction( 'PAN', 2 );
		this.setMouseAction( 'ZOOM', 'WHEEL' );


		/**
		 * 控制器的视图焦点。
		 *
		 * @type {Vector3}
		 */
		this.target = new Vector3();


		/**
		 * 旋转中心默认不注入，保持旧行为：围绕 controls.target 旋转。
		 *
		 * @type {?Function}
		 */
		this.rotationCenterResolver = null;
		this._rotationCenter = new Vector3();
		this._rotationCenterTargetOffset = new Vector3();


		// 内部运行状态。

		this.state = CameraControlsControlState.NONE;
		this.keyState = CameraControlsControlState.NONE;

		this._lastPosition = new Vector3();
		this._lastZoom = 1;
		this._touchZoomDistanceStart = 0;
		this._touchZoomDistanceEnd = 0;
		this._lastAngle = 0;

		this._eye = new Vector3();

		this._movePrev = new Vector2();
		this._moveCurr = new Vector2();

		this._lastAxis = new Vector3();

		this._zoomStart = new Vector2();
		this._zoomEnd = new Vector2();


		this._wheelZoomFactor = 1;


		this._cursorZoomActive = false;
		this._cursorZoomPoint = new Vector3();


		this._panStart = new Vector2();
		this._panEnd = new Vector2();


		this._mousePanActive = false;
		this._mousePanCursor = new Vector2();
		this._mousePanStart = new Vector3();
		this._mousePanEnd = new Vector3();


		this._pointers = [];
		this._pointerPositions = {};

		// 事件监听函数需要提前绑定 this，便于后续注册和移除。

		this._onPointerMove = onPointerMove.bind( this );
		this._onPointerDown = onPointerDown.bind( this );
		this._onPointerUp = onPointerUp.bind( this );
		this._onPointerCancel = onPointerCancel.bind( this );
		this._onContextMenu = onContextMenu.bind( this );
		this._onMouseWheel = onMouseWheel.bind( this );
		this._onKeyDown = onKeyDown.bind( this );
		this._onKeyUp = onKeyUp.bind( this );

		this._onTouchStart = onTouchStart.bind( this );
		this._onTouchMove = onTouchMove.bind( this );
		this._onTouchEnd = onTouchEnd.bind( this );

		this._onMouseDown = onMouseDown.bind( this );
		this._onMouseMove = onMouseMove.bind( this );
		this._onMouseUp = onMouseUp.bind( this );

		// reset 使用的初始状态快照。

		this._target0 = this.target.clone();
		this._position0 = this.object.position.clone();
		this._up0 = this.object.up.clone();
		this._zoom0 = this.object.zoom;

		if ( domElement !== null ) {

			this.connect( domElement );

			this.handleResize();

		}

		// 初始化完成后立即更新一次，确保相机状态与内部缓存一致。
		this.update();

	}

	connect( element: HTMLElement | SVGElement ): void {

		super.connect( element );

		window.addEventListener( 'keydown', this._onKeyDown );
		window.addEventListener( 'keyup', this._onKeyUp );

		this.domElement.addEventListener( 'pointerdown', this._onPointerDown );
		this.domElement.addEventListener( 'pointercancel', this._onPointerCancel );
		this.domElement.addEventListener( 'wheel', this._onMouseWheel, { passive: false } );
		this.domElement.addEventListener( 'contextmenu', this._onContextMenu );

		this.domElement.style.touchAction = 'none'; // 禁用触控滚动，避免浏览器默认手势干扰控制器。

	}

	disconnect(): void {

		// 断开是输入所有权终点，不能在重连或手动 update 时复活旧运动。
		this.cancelActiveInteraction();

		window.removeEventListener( 'keydown', this._onKeyDown );
		window.removeEventListener( 'keyup', this._onKeyUp );

		this.domElement.removeEventListener( 'pointerdown', this._onPointerDown );
		this.domElement.ownerDocument.removeEventListener( 'pointermove', this._onPointerMove );
		this.domElement.ownerDocument.removeEventListener( 'pointerup', this._onPointerUp );
		this.domElement.removeEventListener( 'pointercancel', this._onPointerCancel );
		this.domElement.removeEventListener( 'wheel', this._onMouseWheel );
		this.domElement.removeEventListener( 'contextmenu', this._onContextMenu );

		this.domElement.style.touchAction = ''; // 恢复触控滚动配置。

	}

	dispose(): void {

		this.disconnect();

	}

	/**
	 * 取消当前指针驱动的相机交互。
	 *
	 * 设计说明：
	 * 组件拖拽会临时禁用相机控制器；如果禁用发生在 CameraControls 收到 pointerdown 之后，
	 * 原本的 pointerup 会因为 enabled=false 被跳过，导致 state 与 document 级监听残留。
	 * 因此禁用或恢复控制器前需要显式清理一次内部指针会话，但不能重置相机位置。
	 */
	cancelActiveInteraction(): void {

		this._stopMotion();

		const hadActiveInteraction = this.state !== CameraControlsControlState.NONE ||
			this.keyState !== CameraControlsControlState.NONE ||
			this._pointers.length > 0 ||
			this._mousePanActive ||
			this._cursorZoomActive;

		this.state = CameraControlsControlState.NONE;
		this.keyState = CameraControlsControlState.NONE;
		this._cursorZoomActive = false;
		this._mousePanActive = false;
		this._panStart.copy( this._panEnd );

		this._pointers.forEach( ( pointer ) => {

			if ( this.domElement.hasPointerCapture?.( pointer.pointerId ) ) {

				this.domElement.releasePointerCapture( pointer.pointerId );

			}

		} );
		this._pointers.length = 0;
		Object.keys( this._pointerPositions ).forEach( ( pointerId ) => {

			delete this._pointerPositions[ Number( pointerId ) ];

		} );

		this.domElement.ownerDocument.removeEventListener( 'pointermove', this._onPointerMove );
		this.domElement.ownerDocument.removeEventListener( 'pointerup', this._onPointerUp );
		window.addEventListener( 'keydown', this._onKeyDown );

		if ( hadActiveInteraction ) {

			this.dispatchEvent( _endEvent );

		}

	}

	/**
	 * 清空当前运动增量，但不改变相机位姿、指针捕获或事件会话。
	 * 新手势接管与显式取消共用此出口，避免各入口只清理一种残差。
	 * @param preserveWheel 是否保留同帧尚未消费的滚轮累计倍率。
	 */
	_stopMotion( preserveWheel = false ): void {
		this._lastAngle = 0;
		this.rotationCenterCaptured = false;
		this._movePrev.copy( this._moveCurr );
		this._zoomStart.copy( this._zoomEnd );
		this._panStart.copy( this._panEnd );
		this._mousePanStart.copy( this._mousePanEnd );
		this._touchZoomDistanceStart = this._touchZoomDistanceEnd;
		if ( ! preserveWheel ) this._wheelZoomFactor = 1;
	}

	/**
	 * 按剩余触点初始化新的触控手势，统一新增/抬起触点后的状态交接。
	 * 触点当前位置来自统一跟踪表，不能读取已抬起事件或旧 pointerdown 坐标。
	 */
	_startTouchGesture(): void {
		this._stopMotion();
		this.handleResize();
		this._mousePanActive = false;
		const first = this._pointerPositions[ this._pointers[ 0 ].pointerId ];
		if ( this._pointers.length === 1 ) {
			this.state = CameraControlsControlState.TOUCH_ROTATE;
			this._moveCurr.copy( this._getMouseOnCircle( first.x, first.y ) );
			this._movePrev.copy( this._moveCurr );
		} else {
			this.state = CameraControlsControlState.TOUCH_ZOOM_PAN;
			const second = this._pointerPositions[ this._pointers[ 1 ].pointerId ];
			this._touchZoomDistanceEnd = this._touchZoomDistanceStart = first.distanceTo( second );
			this._panStart.copy( this._getMouseOnScreen( ( first.x + second.x ) / 2, ( first.y + second.y ) / 2 ) );
			this._panEnd.copy( this._panStart );
		}
		this.dispatchEvent( _startEvent );
		if ( this.isRotationInteractionActive() ) this._resolveRotationCenter();
	}

	/**
	 * 应用窗口尺寸变化后必须调用该方法刷新控制器屏幕区域。
	 */
	handleResize(): void {

		const box = this.domElement.getBoundingClientRect();
		// 这里沿用 jQuery offset() 类似的偏移修正方式，保证页面滚动后坐标仍然准确。
		const d = this.domElement.ownerDocument.documentElement;

		this.screen.left = box.left + window.pageXOffset - d.clientLeft;
		this.screen.top = box.top + window.pageYOffset - d.clientTop;
		this.screen.width = box.width;
		this.screen.height = box.height;

	}

	update(): void {

		this._eye.subVectors( this.object.position, this.target );

		if ( ! this.noRotate ) {

			this._rotateCamera();

		}

		if ( ! this.noZoom ) {

			this._zoomCamera();

		}

		if ( ! this.noPan ) {

			this._panCamera();

		}

		this.object.position.addVectors( this.target, this._eye );

		if ( isPerspectiveCamera( this.object ) ) {

			this._checkDistances();

			this.object.lookAt( this.target );

			if ( this._lastPosition.distanceToSquared( this.object.position ) > _EPS ) {

				this.dispatchEvent( _changeEvent );

				this._lastPosition.copy( this.object.position );

			}

		} else if ( isOrthographicCamera( this.object ) ) {

			this.object.lookAt( this.target );

			if ( this._lastPosition.distanceToSquared( this.object.position ) > _EPS || this._lastZoom !== this.object.zoom ) {

				this.dispatchEvent( _changeEvent );

				this._lastPosition.copy( this.object.position );
				this._lastZoom = this.object.zoom;

			}

		} else {

			console.warn( 'THREE.CameraControls: Unsupported camera type.' );

		}

	}

	/**
	 * 将控制器重置为构造时记录的初始状态。
	 */
	reset(): void {

		this.cancelActiveInteraction();

		this.state = CameraControlsControlState.NONE;
		this.keyState = CameraControlsControlState.NONE;

		this.target.copy( this._target0 );
		this.object.position.copy( this._position0 );
		this.object.up.copy( this._up0 );
		this.object.zoom = this._zoom0;

		this.object.updateProjectionMatrix();

		this._eye.subVectors( this.object.position, this.target );

		this.object.lookAt( this.target );

		this.dispatchEvent( _changeEvent );

		this._lastPosition.copy( this.object.position );
		this._lastZoom = this.object.zoom;

	}


	/**
	 * 设置鼠标输入和相机操作之间的映射关系。
	 *
	 * @param operation - 鼠标输入需要触发的相机操作。
	 * @param mouse - 鼠标按钮或滚轮输入。
	 * @param key - 可选修饰键。
	 * @returns 如果映射设置成功则返回 `true`，否则返回 `false`。
	 */
	setMouseAction(
		operation: CameraControlsMouseActionOperation,
		mouse: CameraControlsMouseActionMouse,
		key: CameraControlsMouseActionKey | null = null
	): boolean {
		// 校验输入，避免无效配置污染动作表。
		if ( ! isValidMouseOperation( operation ) || ! isValidMouseInput( mouse ) || ! isValidMouseModifier( key ) ) {
			return false;
		}

		// 使用统一结构保存动作，便于后续按鼠标输入和修饰键精确查找。
		const mouseAction = {
			operation: operation,
			mouse: mouse,
			key: key,
			state: _OPERATION_STATE[ operation ]
		};

		// 同一个鼠标输入和修饰键组合只保留一条映射，新的配置替换旧配置。
		for ( let i = 0; i < this.mouseActions.length; i ++ ) {

			if ( this.mouseActions[ i ].mouse === mouse && this.mouseActions[ i ].key === key ) {

				this.mouseActions.splice( i, 1, mouseAction );
				return true;

			}

		}

		// 没有冲突时追加新映射。
		this.mouseActions.push( mouseAction );
		return true;

	}

	/**
	 * 移除指定鼠标输入和修饰键组合的操作映射。
	 *
	 * @param mouse - 鼠标按钮或滚轮输入。
	 * @param key - 可选修饰键。
	 * @returns 如果成功移除映射则返回 `true`，否则返回 `false`。
	 */
	unsetMouseAction( mouse: CameraControlsMouseActionMouse, key: CameraControlsMouseActionKey | null = null ): boolean {

		// 校验输入，避免误删动作表。
		if ( ! isValidMouseInput( mouse ) || ! isValidMouseModifier( key ) ) {

			return false;

		}

		// 只移除与鼠标输入和修饰键完全匹配的映射。
		for ( let i = 0; i < this.mouseActions.length; i ++ ) {

			if ( this.mouseActions[ i ].mouse === mouse && this.mouseActions[ i ].key === key ) {

				this.mouseActions.splice( i, 1 );
				return true;

			}

		}

		return false;

	}

	/**
	 * 根据鼠标输入和修饰键获取对应的内部控制状态。
	 *
	 * @param mouse - 鼠标按钮或滚轮输入。
	 * @param key - 当前修饰键。
	 * @returns 匹配到的内部控制状态。
	 */
	_getStateFromMouseAction(
		mouse: CameraControlsMouseActionMouse,
		key: CameraControlsMouseActionKey | null
	): CameraControlsControlState {

		// 优先查找带修饰键的精确映射。
		for ( let i = 0; i < this.mouseActions.length; i ++ ) {

			const action = this.mouseActions[ i ];

			if ( action.mouse === mouse && action.key === key ) {

				return action.state;

			}

		}

		// 如果按下了修饰键但没有精确映射，则回退到无修饰键映射。
		if ( key !== null ) {

			for ( let i = 0; i < this.mouseActions.length; i ++ ) {

				const action = this.mouseActions[ i ];

				if ( action.mouse === mouse && action.key === null ) {

					return action.state;

				}

			}

		}

		return CameraControlsControlState.NONE;

	}


	/**
	 * 设置旋转中心解析器。
	 *
	 * 设计说明：
	 * 1. 旋转中心属于 Viewer 场景语义，CameraControls 只通过函数协议获取坐标；
	 * 2. 传入 `null` 会恢复旧逻辑，即围绕当前 `target` 旋转；
	 * 3. 解析器只影响旋转，不改变缩放和平移的目标平面语义。
	 *
	 * @param resolver 旋转中心解析器；为空时清除外部解析器。
	 */
	setRotationCenterResolver( resolver: CameraControlsRotationCenterResolver | null ): void {

		this.rotationCenterResolver = resolver;

	}

	/**
	 * 判断当前控制器是否处于旋转输入状态。
	 *
	 * 设计说明：
	 * 1. CameraManager 只需要知道当前交互是否会消费旋转中心，不应直接依赖内部状态枚举；
	 * 2. 鼠标按键与键盘覆盖状态都可能触发旋转，因此必须统一读取最终生效状态；
	 * 3. `noRotate` 禁用时即使输入映射仍指向旋转，也不能让外部创建旋转中心锁；
	 * 4. 该方法只暴露只读语义，不允许外部修改控制器状态机。
	 *
	 * @returns 当前交互是否正在执行旋转输入。
	 */
	isRotationInteractionActive(): boolean {

		if ( this.noRotate ) {

			return false;

		}

		const state = ( this.keyState !== CameraControlsControlState.NONE ) ? this.keyState : this.state;
		return state === CameraControlsControlState.ROTATE ||
			state === CameraControlsControlState.TOUCH_ROTATE;

	}

	/**
	 * 解析本帧旋转中心。
	 *
	 * @returns 当前旋转中心。
	 */
	_resolveRotationCenter(): Vector3 {

		// 一个手势及其惯性共用同一中心；抬起后的上层锁释放不应触发场景重算。
		if ( this.rotationCenterCaptured ) return this._rotationCenter;

		if ( this.rotationCenterResolver ) {

			// 上层解析器可以复用缓存写入，也可以返回另一个 Vector3；这里统一复制到内部缓存。
			const resolvedCenter = this.rotationCenterResolver( this._rotationCenter );
			if ( resolvedCenter ) {

				this._rotationCenter.copy( resolvedCenter );
				this.rotationCenterCaptured = true;
				return this._rotationCenter;

			}

		}

		// 未注入解析器时保持原有行为，避免 CameraControls 单独使用时出现行为破坏。
		this.rotationCenterCaptured = true;
		return this._rotationCenter.copy( this.target );

	}

	/**
	 * 围绕当前旋转中心应用相机旋转。
	 *
	 * 设计说明：
	 * 1. 参考 `reference/ArcballControls.js` 的做法，旋转应围绕一个稳定世界点执行；
	 * 2. 当前控制器最终会通过 `position = target + eye` 写回相机位置，因此需要同步旋转 `target` 与 `_eye`；
	 * 3. 同步旋转 target 可以保留用户平移后的屏幕构图，不会在开始旋转时突然把视线吸回旋转中心。
	 *
	 * @param quaternion 本次旋转四元数。
	 */
	_applyRotationAroundResolvedCenter( quaternion: Quaternion ): void {

		const rotationCenter = this._resolveRotationCenter();

		// target 作为观察点，需要绕同一个世界旋转中心变换，保持相机视线和屏幕构图连续。
		this.target.copy(
			this._rotationCenterTargetOffset
				.copy( this.target )
				.sub( rotationCenter )
				.applyQuaternion( quaternion )
				.add( rotationCenter )
		);

		// _eye 表示相机相对 target 的偏移；同步旋转后最终相机位置等价于绕 rotationCenter 旋转。
		this._eye.applyQuaternion( quaternion );
		this.object.up.applyQuaternion( quaternion );

	}


	_panCamera(): void {


		if ( this._mousePanActive ) {

			_pan.copy( this._mousePanStart ).sub( this._mousePanEnd );

			if ( _pan.lengthSq() ) {

				this.object.position.add( _pan );
				this.target.add( _pan );

				this._mousePanStart.copy( this._getMouseOnTargetPlane( this._mousePanCursor.x, this._mousePanCursor.y ) );
				this._mousePanEnd.copy( this._mousePanStart );
				this._panStart.copy( this._panEnd );

			}

			return;

		}


		_mouseChange.copy( this._panEnd ).sub( this._panStart );

		if ( _mouseChange.lengthSq() ) {

			if ( isOrthographicCamera( this.object ) ) {

				const scale_x = ( this.object.right - this.object.left ) / this.object.zoom / this.domElement.clientWidth;
				const scale_y = ( this.object.top - this.object.bottom ) / this.object.zoom / this.domElement.clientWidth;

				_mouseChange.x *= scale_x;
				_mouseChange.y *= scale_y;

			}

			_mouseChange.multiplyScalar( this._eye.length() * this.panSpeed );

			_pan.copy( this._eye ).cross( this.object.up ).setLength( _mouseChange.x );
			_pan.add( _objectUp.copy( this.object.up ).setLength( _mouseChange.y ) );

			this.object.position.add( _pan );
			this.target.add( _pan );

			if ( this.staticMoving ) {

				this._panStart.copy( this._panEnd );

			} else {

				this._panStart.add( _mouseChange.subVectors( this._panEnd, this._panStart ).multiplyScalar( this.dynamicDampingFactor ) );

			}

		}

	}

	_rotateCamera(): void {

		_moveDirection.set( this._moveCurr.x - this._movePrev.x, this._moveCurr.y - this._movePrev.y, 0 );
		let angle = _moveDirection.length();

		if ( angle ) {

			this._eye.copy( this.object.position ).sub( this.target );

			_eyeDirection.copy( this._eye ).normalize();
			_objectUpDirection.copy( this.object.up ).normalize();
			_objectSidewaysDirection.crossVectors( _objectUpDirection, _eyeDirection ).normalize();

			_objectUpDirection.setLength( this._moveCurr.y - this._movePrev.y );
			_objectSidewaysDirection.setLength( this._moveCurr.x - this._movePrev.x );

			_moveDirection.copy( _objectUpDirection.add( _objectSidewaysDirection ) );

			_axis.crossVectors( _moveDirection, this._eye ).normalize();

			angle *= this.rotateSpeed;
			_quaternion.setFromAxisAngle( _axis, angle );


			// 旋转不再隐式围绕 target，而是围绕上层解析出的稳定旋转中心。
			this._applyRotationAroundResolvedCenter( _quaternion );


			this._lastAxis.copy( _axis );
			this._lastAngle = angle;

		} else if ( ! this.staticMoving && this._lastAngle ) {

			const damping = Math.sqrt( 1.0 - this.dynamicDampingFactor );
			this._lastAngle *= damping;
			let inertiaAngle = this._lastAngle;
			if ( damping < 1 && Math.abs( inertiaAngle ) < ROTATION_INERTIA_ANGLE_EPSILON ) {
				// 一次消费剩余级数而非截掉尾角，保留原数学极限并终止后续空转。
				inertiaAngle /= 1 - damping;
				this._lastAngle = 0;
			}
			this._eye.copy( this.object.position ).sub( this.target );
			_quaternion.setFromAxisAngle( this._lastAxis, inertiaAngle );

			// 惯性旋转必须沿用同一旋转中心，避免松手后的阻尼阶段绕回 target。
			this._applyRotationAroundResolvedCenter( _quaternion );
			if ( this._lastAngle === 0 && ! this.isRotationInteractionActive() ) {
				this.rotationCenterCaptured = false;
			}


		}

		this._movePrev.copy( this._moveCurr );

	}

	_zoomCamera(): void {

		let factor: number;


		if ( this._wheelZoomFactor !== 1 ) {

			factor = this._wheelZoomFactor;
			this._wheelZoomFactor = 1;

			if ( factor > 0.0 ) {

				if ( isPerspectiveCamera( this.object ) ) {


					this._applyPerspectiveZoom( factor );


				} else if ( isOrthographicCamera( this.object ) ) {


					this._applyOrthographicZoom( factor );


				} else {

					console.warn( 'THREE.CameraControls: Unsupported camera type' );

				}

			}

			this._cursorZoomActive = false;
			return;

		}


		if ( this.state === CameraControlsControlState.TOUCH_ZOOM_PAN ) {

			factor = this._touchZoomDistanceStart / this._touchZoomDistanceEnd;
			this._touchZoomDistanceStart = this._touchZoomDistanceEnd;

			if ( isPerspectiveCamera( this.object ) ) {


				this._applyPerspectiveZoom( factor );


			} else if ( isOrthographicCamera( this.object ) ) {


				this._applyOrthographicZoom( factor );


			} else {

				console.warn( 'THREE.CameraControls: Unsupported camera type' );

			}

		} else {

			factor = 1.0 + ( this._zoomEnd.y - this._zoomStart.y ) * this.zoomSpeed;

			if ( factor !== 1.0 && factor > 0.0 ) {

				if ( isPerspectiveCamera( this.object ) ) {


					this._applyPerspectiveZoom( factor );


				} else if ( isOrthographicCamera( this.object ) ) {


					this._applyOrthographicZoom( factor );


				} else {

					console.warn( 'THREE.CameraControls: Unsupported camera type' );

				}

			}

			if ( this.staticMoving ) {

				this._zoomStart.copy( this._zoomEnd );

			} else {

				this._zoomStart.y += ( this._zoomEnd.y - this._zoomStart.y ) * this.dynamicDampingFactor;

			}

		}

	}


	/**
	 * 对透视相机应用缩放倍率。
	 *
	 * @param factor - 请求应用的缩放倍率，值越大表示相机离目标点越远。
	 */
	_applyPerspectiveZoom( factor: number ): void {

		// 缩放倍率必须是有限正数，避免异常触控距离把相机推到不可恢复的位置。
		if ( Number.isFinite( factor ) === false || factor <= 0 ) {

			return;

		}

		const currentDistance = this._eye.length();

		if ( currentDistance <= _EPS ) {

			return;

		}

		// 距离限制会改变真实缩放倍率，光标补偿必须使用实际倍率才能保持鼠标下世界点稳定。
		const nextDistance = MathUtils.clamp( currentDistance * factor, this.minDistance, this.maxDistance );
		const appliedFactor = nextDistance / currentDistance;

		this._eye.multiplyScalar( appliedFactor );
		this._applyCursorZoom( appliedFactor );

	}

	/**
	 * 对正交相机应用缩放倍率。
	 *
	 * @param factor - 请求应用的缩放倍率，值越大表示视口显示范围越大。
	 */
	_applyOrthographicZoom( factor: number ): void {

		// 缩放倍率必须是有限正数，避免异常触控距离把正交 zoom 写成无效值。
		if ( Number.isFinite( factor ) === false || factor <= 0 ) {

			return;

		}

		const previousZoom = this.object.zoom;

		this.object.zoom = MathUtils.clamp( previousZoom / factor, this.minZoom, this.maxZoom );

		if ( this._lastZoom !== this.object.zoom ) {

			this.object.updateProjectionMatrix();

		}

		if ( previousZoom <= _EPS || this.object.zoom <= _EPS ) {

			return;

		}

		// 正交相机的真实缩放倍率由 zoom 前后比例决定，不能直接使用请求倍率。
		this._applyCursorZoom( previousZoom / this.object.zoom );

	}


	_getMouseOnScreen( pageX: number, pageY: number ): Vector2 {

		_v2.set(
			( pageX - this.screen.left ) / this.screen.width,
			( pageY - this.screen.top ) / this.screen.height
		);

		return _v2;

	}

	_getMouseOnCircle( pageX: number, pageY: number ): Vector2 {

		_v2.set(
			( ( pageX - this.screen.width * 0.5 - this.screen.left ) / ( this.screen.width * 0.5 ) ),
			( ( this.screen.height + 2 * ( this.screen.top - pageY ) ) / this.screen.width ) // 这里沿用 CameraControls 算法，刻意使用屏幕宽度归一化纵向坐标。
		);

		return _v2;

	}


	/**
	 * 获取鼠标所在位置投影到目标平面后的世界坐标。
	 *
	 * @param pageX - 鼠标在页面中的横向坐标。
	 * @param pageY - 鼠标在页面中的纵向坐标。
	 * @returns 光标投影到目标平面后的世界坐标。
	 */
	_getMouseOnTargetPlane( pageX: number, pageY: number ): Vector3 {

		// 光标反投影直接依赖画布尺寸，交互入口刷新一次可避免初始化阶段缓存尺寸滞后。
		this.handleResize();

		// 正交相机初始化阶段可能尚未经历完整渲染帧，反投影前必须确保矩阵世界已同步。
		this.object.updateMatrixWorld( true );

		// 将页面坐标转换为标准设备坐标，方便后续执行相机反投影。
		_cursorZoomNDC.set(
			( ( pageX - this.screen.left ) / this.screen.width ) * 2 - 1,
			- ( ( pageY - this.screen.top ) / this.screen.height ) * 2 + 1,
			0.5
		);

		// 目标平面始终垂直于当前相机视线方向，并经过 controls.target。
		this.object.getWorldDirection( _cursorZoomPlaneNormal );

		if ( isPerspectiveCamera( this.object ) ) {

			// 透视相机从相机位置发射穿过光标的射线。
			_cursorZoomRayOrigin.copy( this.object.position );
			_cursorZoomRayDirection.copy( _cursorZoomNDC ).unproject( this.object ).sub( _cursorZoomRayOrigin ).normalize();

		} else if ( isOrthographicCamera( this.object ) ) {

			// 正交相机的射线方向固定为相机朝向，射线起点为光标所在的反投影点。
			_cursorZoomRayOrigin.copy( _cursorZoomNDC ).unproject( this.object );
			_cursorZoomRayDirection.copy( _cursorZoomPlaneNormal );

		} else {

			// 不支持的相机类型直接回退到 target，避免产生无效平移。
			return _cursorZoomRayOrigin.copy( this.target );

		}

		// 计算射线与目标平面的交点。
		const denominator = _cursorZoomRayDirection.dot( _cursorZoomPlaneNormal );

		if ( Math.abs( denominator ) < _EPS ) {

			return _cursorZoomRayOrigin.copy( this.target );

		}

		const distance = _cursorZoomDelta.copy( this.target ).sub( _cursorZoomRayOrigin ).dot( _cursorZoomPlaneNormal ) / denominator;

		return _cursorZoomRayOrigin.add( _cursorZoomRayDirection.multiplyScalar( distance ) );

	}

	/**
	 * 记录当前光标缩放中心。
	 *
	 * @param pageX - 鼠标在页面中的横向坐标。
	 * @param pageY - 鼠标在页面中的纵向坐标。
	 */
	_setCursorZoomPoint( pageX: number, pageY: number ): void {

		// 只有允许平移时才能通过移动 target 保持光标位置不变。
		this._cursorZoomActive = this.cursorZoom === true && this.noPan === false;

		if ( this._cursorZoomActive ) {

			this._cursorZoomPoint.copy( this._getMouseOnTargetPlane( pageX, pageY ) );

		}

	}

	/**
	 * 根据本次缩放比例补偿相机和 target，使光标下的世界点保持稳定。
	 *
	 * @param factor - 本次 CameraControls 缩放比例。
	 */
	_applyCursorZoom( factor: number ): void {

		if ( this._cursorZoomActive === false || this.cursorZoom === false || this.noPan === true ) {

			return;

		}

		// 缩放围绕光标点进行时，target 需要按同一比例向光标点补偿。
		this.target.add( _cursorZoomDelta.copy( this._cursorZoomPoint ).sub( this.target ).multiplyScalar( 1 - factor ) );

	}


	_addPointer( event: PointerEvent ): void {

		this._pointers.push( event );

	}

	_removePointer( event: PointerEvent ): void {

		delete this._pointerPositions[ event.pointerId ];

		for ( let i = 0; i < this._pointers.length; i ++ ) {

			if ( this._pointers[ i ].pointerId === event.pointerId ) {

				this._pointers.splice( i, 1 );
				return;

			}

		}

	}

	_trackPointer( event: PointerEvent ): void {

		let position = this._pointerPositions[ event.pointerId ];

		if ( position === undefined ) {

			position = new Vector2();
			this._pointerPositions[ event.pointerId ] = position;

		}

		position.set( event.pageX, event.pageY );

	}

	_getSecondPointerPosition( event: PointerEvent ): Vector2 {

		const pointer = ( event.pointerId === this._pointers[ 0 ].pointerId ) ? this._pointers[ 1 ] : this._pointers[ 0 ];

		return this._pointerPositions[ pointer.pointerId ];

	}

	_checkDistances(): void {

		if ( ! this.noZoom || ! this.noPan ) {

			if ( this._eye.lengthSq() > this.maxDistance * this.maxDistance ) {

				this.object.position.addVectors( this.target, this._eye.setLength( this.maxDistance ) );
				this._zoomStart.copy( this._zoomEnd );

			}

			if ( this._eye.lengthSq() < this.minDistance * this.minDistance ) {

				this.object.position.addVectors( this.target, this._eye.setLength( this.minDistance ) );
				this._zoomStart.copy( this._zoomEnd );

			}

		}

	}

	// ----------------- 额外代码

	/**
	 * 同步相机位置与观察目标。
	 *
	 * @param positionX - 相机位置 X 坐标。
	 * @param positionY - 相机位置 Y 坐标。
	 * @param positionZ - 相机位置 Z 坐标。
	 * @param targetX - 观察目标 X 坐标。
	 * @param targetY - 观察目标 Y 坐标。
	 * @param targetZ - 观察目标 Z 坐标。
	 */
	setLookAt(	positionX: number, positionY: number, positionZ: number,
	              targetX: number, targetY: number, targetZ: number,
	){
		// 绝对视图定位拥有新的运动状态，旧手势及阻尼不能在下一帧覆盖它。
		this.cancelActiveInteraction();
		this.object.position.set(positionX, positionY, positionZ);
		this.target.set(targetX,targetY,targetZ)
		this.object.lookAt( this.target );
		this.object.updateMatrix();
		this.object.updateMatrixWorld( true );
	}


	/**
	 * 同步相机位置并刷新矩阵。
	 *
	 * @param positionX - 相机位置 X 坐标。
	 * @param positionY - 相机位置 Y 坐标。
	 * @param positionZ - 相机位置 Z 坐标。
	 */
	setPosition(	positionX: number, positionY: number, positionZ: number,){
		this.object.position.set(positionX, positionY, positionZ);
		this.object.updateMatrix();
		this.object.updateMatrixWorld( true );
	}

	/**
	 * 同步观察目标并刷新相机朝向。
	 *
	 * @param targetX - 观察目标 X 坐标。
	 * @param targetY - 观察目标 Y 坐标。
	 * @param targetZ - 观察目标 Z 坐标。
	 */
	setTarget(
		targetX: number, targetY: number, targetZ: number,
	){
		this.target.set(targetX,targetY,targetZ)
		this.object.lookAt( this.target );
		this.object.updateMatrix();
		this.object.updateMatrixWorld( true );
	}

	getTarget(v3:Vector3){

		v3.set(this.target.x,this.target.y,this.target.z,)
		return v3
	}
}


/**
 * 判断鼠标操作是否为 CameraControls 支持的操作。
 *
 * @param operation - 需要校验的鼠标操作。
 * @returns 如果操作有效则返回 `true`。
 */
function isValidMouseOperation( operation: unknown ): operation is CameraControlsMouseActionOperation {

	// 只允许旋转、缩放和平移三种操作。
	return operation === CameraControlsMouseOperation.ROTATE ||
		operation === CameraControlsMouseOperation.ZOOM ||
		operation === CameraControlsMouseOperation.PAN;

}

/**
 * 判断鼠标输入是否为 CameraControls 支持的输入。
 *
 * @param mouse - 需要校验的鼠标输入。
 * @returns 如果输入有效则返回 `true`。
 */
function isValidMouseInput( mouse: unknown ): mouse is CameraControlsMouseActionMouse {

	// 只允许左键、中键、右键和滚轮。
	return mouse === CameraControlsMouseInput.LEFT ||
		mouse === CameraControlsMouseInput.MIDDLE ||
		mouse === CameraControlsMouseInput.RIGHT ||
		mouse === CameraControlsMouseInput.WHEEL;

}

/**
 * 判断鼠标修饰键是否为 CameraControls 支持的修饰键。
 *
 * @param key - 需要校验的修饰键。
 * @returns 如果修饰键有效则返回 `true`。
 */
function isValidMouseModifier( key: unknown ): key is CameraControlsMouseActionKey | null {

	// `null` 表示无修饰键，除此之外只允许 Ctrl 和 Shift。
	return key === null || key === CameraControlsMouseModifier.CTRL || key === CameraControlsMouseModifier.SHIFT;

}

/**
 * 从鼠标事件中读取当前使用的修饰键。
 *
 * @param event - 需要读取修饰键的输入事件。
 * @returns 当前修饰键，如果没有修饰键则返回 `null`。
 */
function getMouseModifier( event: MouseEvent | PointerEvent | WheelEvent ): CameraControlsMouseActionKey | null {

	// Ctrl 和 Command 都归为 CTRL，兼容不同操作系统的常见修饰键。
	if ( event.ctrlKey || event.metaKey ) {

		return CameraControlsMouseModifier.CTRL;

	}

	// Shift 单独作为一个可配置修饰键。
	if ( event.shiftKey ) {

		return CameraControlsMouseModifier.SHIFT;

	}

	return null;

}


function onPointerDown(this: CameraControls, event: PointerEvent ): void {

	if ( this.enabled === false ) return;

	if ( this._pointers.length === 0 ) {

		this.domElement.setPointerCapture( event.pointerId );

		this.domElement.ownerDocument.addEventListener( 'pointermove', this._onPointerMove );
		this.domElement.ownerDocument.addEventListener( 'pointerup', this._onPointerUp );

	}

	//

	this._addPointer( event );

	if ( event.pointerType === 'touch' ) {

		this._onTouchStart( event );

	} else {

		if ( this._onMouseDown( event ) === false ) {

			this._removePointer( event );

			if ( this._pointers.length === 0 ) {

				this.domElement.releasePointerCapture( event.pointerId );
				this.domElement.ownerDocument.removeEventListener( 'pointermove', this._onPointerMove );
				this.domElement.ownerDocument.removeEventListener( 'pointerup', this._onPointerUp );

			}

		}

	}

}

function onPointerMove(this: CameraControls, event: PointerEvent ): void {

	if ( this.enabled === false ) return;

	if ( event.pointerType === 'touch' ) {

		this._onTouchMove( event );

	} else {

		this._onMouseMove( event );

	}

}

function onPointerUp(this: CameraControls, event: PointerEvent ): void {

	if ( this.enabled === false ) return;

	// 先移除触点，让后续手势分派只依据仍然按下的指针。
	this._removePointer( event );
	if ( event.pointerType === 'touch' ) {

		this._onTouchEnd( event );

	} else {

		this._onMouseUp();

	}

	//

	if ( this._pointers.length === 0 ) {

		this.domElement.releasePointerCapture( event.pointerId );

		this.domElement.ownerDocument.removeEventListener( 'pointermove', this._onPointerMove );
		this.domElement.ownerDocument.removeEventListener( 'pointerup', this._onPointerUp );

	}

}

function onPointerCancel(this: CameraControls, event: PointerEvent ): void {

	// 浏览器取消意味着整段输入不再可信，必须同步退出监听、捕获与运动残差。
	this.cancelActiveInteraction();

}

function onKeyUp( this: CameraControls ): void {

	if ( this.enabled === false ) return;

	this.keyState = CameraControlsControlState.NONE;

	window.addEventListener( 'keydown', this._onKeyDown );

}

function onKeyDown(this: CameraControls, event: KeyboardEvent ): void {

	if ( this.enabled === false ) return;

	window.removeEventListener( 'keydown', this._onKeyDown );

	if ( this.keyState !== CameraControlsControlState.NONE ) {

		return;

	} else if ( event.code === this.keys[ CameraControlsControlState.ROTATE ] && ! this.noRotate ) {

		this.keyState = CameraControlsControlState.ROTATE;

	} else if ( event.code === this.keys[ CameraControlsControlState.ZOOM ] && ! this.noZoom ) {

		this.keyState = CameraControlsControlState.ZOOM;

	} else if ( event.code === this.keys[ CameraControlsControlState.PAN ] && ! this.noPan ) {

		this.keyState = CameraControlsControlState.PAN;

	}

}

function onMouseDown(this: CameraControls, event: PointerEvent ): boolean {

	const mouseModifier = getMouseModifier( event );
	const mouseButton = event.button as CameraControlsMouseActionMouse;
	this.state = this._getStateFromMouseAction( mouseButton, mouseModifier );

	if ( this.state === CameraControlsControlState.NONE ) {

		return false;

	}

	const state = ( this.keyState !== CameraControlsControlState.NONE ) ? this.keyState : this.state;

	// 已接受的新鼠标手势接管旧运动；未映射的按键不会终止相机惯性。
	this._stopMotion();
	// 起点归一化与光标反投影必须消费同一画布尺寸，不能在二者之间才刷新布局。
	this.handleResize();

	if ( state === CameraControlsControlState.ROTATE && ! this.noRotate ) {

		this._moveCurr.copy( this._getMouseOnCircle( event.pageX, event.pageY ) );
		this._movePrev.copy( this._moveCurr );

	} else if ( state === CameraControlsControlState.ZOOM && ! this.noZoom ) {

		this._zoomStart.copy( this._getMouseOnScreen( event.pageX, event.pageY ) );
		this._zoomEnd.copy( this._zoomStart );
		this._setCursorZoomPoint( event.pageX, event.pageY );

	} else if ( state === CameraControlsControlState.PAN && ! this.noPan ) {

		this._panStart.copy( this._getMouseOnScreen( event.pageX, event.pageY ) );
		this._panEnd.copy( this._panStart );
		this._mousePanActive = true;
		this._mousePanCursor.set( event.pageX, event.pageY );
		this._mousePanStart.copy( this._getMouseOnTargetPlane( event.pageX, event.pageY ) );
		this._mousePanEnd.copy( this._mousePanStart );

	}

	this.dispatchEvent( _startEvent );
	// start 先让上层锁定中心；即便首次移动尚未到渲染帧就抬起也使用同一中心。
	if ( this.isRotationInteractionActive() ) this._resolveRotationCenter();
	return true;

}

function onMouseMove(this: CameraControls, event: PointerEvent ): void {

	const state = ( this.keyState !== CameraControlsControlState.NONE ) ? this.keyState : this.state;

	if ( state === CameraControlsControlState.ROTATE && ! this.noRotate ) {

		this._movePrev.copy( this._moveCurr );
		this._moveCurr.copy( this._getMouseOnCircle( event.pageX, event.pageY ) );

	} else if ( state === CameraControlsControlState.ZOOM && ! this.noZoom ) {

		this._zoomEnd.copy( this._getMouseOnScreen( event.pageX, event.pageY ) );

	} else if ( state === CameraControlsControlState.PAN && ! this.noPan ) {

		this._panEnd.copy( this._getMouseOnScreen( event.pageX, event.pageY ) );
		this._mousePanCursor.set( event.pageX, event.pageY );
		this._mousePanEnd.copy( this._getMouseOnTargetPlane( event.pageX, event.pageY ) );

	}

}

function onMouseUp( this: CameraControls ): void {

	if ( this._mousePanActive ) {

		// 世界平移释放时必须清空屏幕平移增量，避免下一帧回落到普通平移分支后重复移动。
		this._panStart.copy( this._panEnd );

	}

	this.state = CameraControlsControlState.NONE;
	this._cursorZoomActive = false;
	this._mousePanActive = false;

	this.dispatchEvent( _endEvent );

}

function onMouseWheel(this: CameraControls, event: WheelEvent ): void {

	if ( this.enabled === false ) return;

	if ( this.noZoom === true ) return;

	const mouseModifier = getMouseModifier( event );
	const state = this._getStateFromMouseAction( CameraControlsMouseInput.WHEEL, mouseModifier );

	if ( state !== CameraControlsControlState.ZOOM ) {

		return;

	}

	event.preventDefault();
	// 同一帧的滚轮倍率继续累计，但不能继承旧旋转或拖动缩放残差。
	this._stopMotion( true );
	this._setCursorZoomPoint( event.pageX, event.pageY );


	const deltaYFactor = _isMac ? - 1 : - 3;
	const delta = ( event.deltaMode === 1 && ! event.ctrlKey ) ? event.deltaY / deltaYFactor : event.deltaY / ( deltaYFactor * 10 );

	this._wheelZoomFactor *= Math.pow( 0.95, delta * this.dollySpeed );


	this.dispatchEvent( _startEvent );
	this.dispatchEvent( _endEvent );

}

function onContextMenu(this: CameraControls, event: Event ): void {

	if ( this.enabled === false ) return;

	event.preventDefault();

}

function onTouchStart(this: CameraControls, event: PointerEvent ): void {

	this._trackPointer( event );
	// 触点数量变化先结束旧中心锁，再初始化新手势。
	if ( this.state !== CameraControlsControlState.NONE ) this.dispatchEvent( _endEvent );
	this._startTouchGesture();

}

function onTouchMove(this: CameraControls, event: PointerEvent ): void {

	this._trackPointer( event );

	switch ( this._pointers.length ) {

		case 1:
			this._movePrev.copy( this._moveCurr );
			this._moveCurr.copy( this._getMouseOnCircle( event.pageX, event.pageY ) );
			break;

		default: // 两个或更多触点共同触发缩放和平移。

			const position = this._getSecondPointerPosition( event );

			const dx = event.pageX - position.x;
			const dy = event.pageY - position.y;
			this._touchZoomDistanceEnd = Math.sqrt( dx * dx + dy * dy );

			const x = ( event.pageX + position.x ) / 2;
			const y = ( event.pageY + position.y ) / 2;
			this._panEnd.copy( this._getMouseOnScreen( x, y ) );
			break;

	}

}

function onTouchEnd(this: CameraControls, event: PointerEvent ): void {
	this.state = CameraControlsControlState.NONE;
	this.dispatchEvent( _endEvent );
	// 最后触点释放保留本次有效惯性；还有触点则按其最新位置接管。
	if ( this._pointers.length > 0 ) this._startTouchGesture();

}

